<template>
  <section
    class="bg-[#0F0F0F] text-white pb-16 lg:px-20 rounded-2xl max-w-[90%] lg:max-w-[75%] mx-auto justify-center flex"
  >
    <UiAlert v-model="showAlert" :alert="alertData" :auto-close="true" />
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-y-16 gap-x-8 items-start">
      <!-- Başlık -->
      <div class="space-y-6 lg:pl-8 items-center">
        <h1 class="text-3xl font-extrabold text-white">
          {{ t("onetoonecontact.title") }}
        </h1>

        <!-- Açıklama -->
        <p class="text-lg leading-8 text-gray-300">
          {{ t("onetoonecontact.subtitle") }}
        </p>
        <p class="text-base text-gray-400">
          {{ t("onetoonecontact.description") }}
        </p>

        <!-- Sağlananlar -->
        <div class="space-y-2 text-base">
          <h2 class="font-semibold text-white text-lg">
            {{ t("onetoonecontact.whatWeProvide") }}
          </h2>
          <ul class="list-disc pl-5 space-y-1 text-gray-300">
            <li>✅ {{ t("onetoonecontact.bullet1") }}</li>
            <li>✅ {{ t("onetoonecontact.bullet2") }}</li>
            <li>✅ {{ t("onetoonecontact.bullet3") }}</li>
            <li>✅ {{ t("onetoonecontact.bullet4") }}</li>
            <li>✅ {{ t("onetoonecontact.bullet5") }}</li>
          </ul>
        </div>

        <!-- Bilgi ve Yönlendirme -->
        <p class="text-gray-400">📍 {{ t("onetoonecontact.locationNote") }}</p>
        <p class="text-white font-semibold">
          📲 {{ t("onetoonecontact.callToAction") }}
        </p>
      </div>
      <!-- Başvuru Formu -->
      <div id="form-section" class="space-y-4 w-full lg:max-w-4xl items-center">
        <!-- Form kartı -->
        <div
          class="w-full rounded-2xl border border-gray-700 bg-gradient-to-br from-[#121212] to-[#1a1a1a] shadow-xl ring-1 ring-gray-800/30 p-6 sm:p-8 space-y-6 backdrop-blur"
        >
          <form class="space-y-5" @submit.prevent="submitForm">
            <!-- Ad Soyad -->
            <div class="space-y-1">
              <label
                for="name"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
              >
                {{ t("onetoonecontact.form.name") }}
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white placeholder:text-gray-400 px-4 py-3 rounded-xl shadow-inner transition-all"
                :placeholder="t('onetoonecontact.form.namePlaceholder')"
              />
            </div>

            <!-- Age -->
            <div class="space-y-1">
              <label
                for="age"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
              >
                {{ t("onetoonecontact.form.age") }}
              </label>
              <input
                id="age"
                v-model="form.age"
                type="number"
                min="4"
                max="80"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white placeholder:text-gray-400 px-4 py-3 rounded-xl shadow-inner transition-all"
                :placeholder="t('onetoonecontact.form.agePlaceholder')"
              />
            </div>
            <!-- Telefon -->
            <div class="space-y-1">
              <label
                for="phone"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
              >
                {{ t("onetoonecontact.form.phone") }}
              </label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white placeholder:text-gray-400 px-4 py-3 rounded-xl shadow-inner transition-all"
                :placeholder="t('onetoonecontact.form.phonePlaceholder')"
              />
            </div>
            <div class="space-y-1">
              <label
                for="forWhom"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
                >{{ t("onetoonecontact.form.forWhom") }}</label
              >
              <select
                id="forWhom"
                v-model="form.forWhom"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white px-4 py-3 rounded-xl shadow-lg transition-all"
              >
                <option value="self">
                  {{ t("onetoonecontact.form.myself") }}
                </option>
                <option value="child">
                  {{ t("onetoonecontact.form.child") }}
                </option>
              </select>
            </div>
            <div class="space-y-1">
              <label
                for="goal"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
                >{{ t("onetoonecontact.form.goal") }}</label
              >
              <textarea
                id="goal"
                v-model="form.goal"
                rows="2"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white placeholder:text-gray-400 px-4 py-3 rounded-xl shadow-inner transition-all"
                :placeholder="t('onetoonecontact.form.goalPlaceholder')"
              />
            </div>
            <!-- Sürüş Seviyesi -->
            <div class="space-y-1">
              <label
                for="level"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
              >
                {{ t("onetoonecontact.form.level") }}
              </label>
              <select
                id="level"
                v-model="form.level"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white px-4 py-3 rounded-xl shadow-lg transition-all"
              >
                <option value="beginner">
                  {{ t("onetoonecontact.form.beginner") }}
                </option>
                <option value="intermediate">
                  {{ t("onetoonecontact.form.intermediate") }}
                </option>
                <option value="advanced">
                  {{ t("onetoonecontact.form.advanced") }}
                </option>
              </select>
            </div>

            <div class="space-y-1">
              <label
                for="still"
                class="block text-xs font-semibold tracking-wider text-gray-400 uppercase"
              >
                {{ t("onetoonecontact.form.style") }}
              </label>
              <select
                id="level"
                v-model="form.style"
                class="w-full bg-[#131313] border border-gray-700 focus:border-accent focus:ring-2 focus:ring-accent/60 text-white px-4 py-3 rounded-xl shadow-lg transition-all"
              >
                <option value="enduro">
                  {{ t("onetoonecontact.form.enduro") }}
                </option>
                <option value="superEnduro">
                  {{ t("onetoonecontact.form.superEnduro") }}
                </option>
                <option value="motocross">
                  {{ t("onetoonecontact.form.motocross") }}
                </option>
              </select>
            </div>

            <!-- Submit Butonu -->
            <div>
              <button
                type="submit"
                class="w-full bg-gradient-to-r from-[#3DCBB1] to-[#2b5e54] hover:brightness-110 active:scale-[0.98] transition-all font-bold tracking-wide px-6 py-3 rounded-xl text-white text-center shadow-[0_4px_20px_rgba(73,167,94,0.6)]"
              >
                {{ t("onetoonecontact.submit") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useSupabaseClient, useI18n } from "#imports";
import UiAlert from "@/components/ui/UiAlert.vue";

const supabase = useSupabaseClient();
const { t } = useI18n();
const form = ref({
  name: "",
  age: "",
  phone: "",
  forWhom: "self",
  goal: "",
  level: "beginner",
  style: "enduro",
});
const loading = ref(false);

// UiAlert state
const showAlert = ref(false);
const alertData = ref({
  message: "",
  type: "info" as "success" | "error" | "warning" | "info",
  icon: "mdi:alert-circle",
});

const notify = (
  message: string,
  type: "success" | "error" | "warning" | "info" = "info",
  icon = "mdi:alert-circle"
) => {
  alertData.value = { message, type, icon };
  showAlert.value = true;
};

const submitForm = async () => {
  if (!form.value.name || !form.value.age || !form.value.phone) {
    notify(t("contact.missing"), "warning");
    return;
  }

  loading.value = true;
  try {
    const { error } = await supabase.from("one_to_one_applications").insert({
      name: form.value.name,
      age: form.value.age,
      phone: form.value.phone,
      for_whom: form.value.forWhom,
      goal: form.value.goal,
      level: form.value.level,
      style: form.value.style,
    });

    if (error) throw error;

    notify(t("contact.success"), "success", "mdi:check-circle");
    form.value = {
      name: "",
      age: "",
      phone: "",
      forWhom: "Kendim için",
      goal: "",
      level: "Yeni Başlayan",
      style: "Enduro",
    };
  } catch (err) {
    console.error(err);
    notify(t("contact.error"), "error", "mdi:alert-circle-outline");
  } finally {
    loading.value = false;
  }
};
</script>
